import type { ChatMessage } from '@command-room/shared';
import { relativeTime } from '@/lib/format';
import { useState } from 'react';

// Safety invariants (do not remove):
// 1. No dangerouslySetInnerHTML — all content rendered as React text nodes (HTML-escaped by default)
// 2. URL_RE only matches https?:// — blocks javascript:, data:, vbscript: from becoming links
// 3. All <a> elements carry rel="noopener noreferrer" + target="_blank"
// 4. No Markdown renderer — plain text only, no embedded HTML interpretation
// 5. Tool/system message content is always rendered as escaped text, never executed

const URL_RE = /https?:\/\/[^\s<>"']+/g;
// Matches Slack file URLs: https://*.slack.com/files/{user}/{file_id}/{name}
const SLACK_FILE_RE = /slack\.com\/files\/[^/]+\/([A-Z0-9]+)\//;

function resolveUrl(url: string, fileMap: Record<string, string>): { href: string; label: string } {
  const match = SLACK_FILE_RE.exec(url);
  const fileId = match?.[1];
  if (fileId && fileMap[fileId]) return { href: fileMap[fileId], label: '📎 View on Google Drive' };
  return { href: url, label: url };
}

function parseContent(text: string): Array<{ kind: 'text' | 'url'; value: string }> {
  const parts: Array<{ kind: 'text' | 'url'; value: string }> = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const idx = match.index!;
    if (idx > last) parts.push({ kind: 'text', value: text.slice(last, idx) });
    parts.push({ kind: 'url', value: match[0] });
    last = idx + match[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });
  return parts;
}

function MessageContent({ text, fileMap }: { text: string; fileMap: Record<string, string> }) {
  return (
    <>
      {parseContent(text).map((part, i) =>
        part.kind === 'url' ? (
          (() => {
            const { href, label } = resolveUrl(part.value, fileMap);
            return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 opacity-90 hover:opacity-100 break-all"
              >
                {label}
              </a>
            );
          })()
        ) : (
          <span key={i}>{part.value}</span>
        ),
      )}
    </>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  agentName: string | null;
  fileMap?: Record<string, string>;
}

export function MessageBubble({ message, agentName, fileMap = {} }: MessageBubbleProps) {
  const { type, content: rawContent, ...rest } = message.message;
  const extraKeys = Object.keys(rest).filter((k) => !['additional_kwargs', 'response_metadata', 'tool_calls', 'invalid_tool_calls'].includes(k));
  const hasExtraData = extraKeys.length > 0;

  const SENDER_RE = /^\[senderName:\s*([^\]]+)\]\s*/;
  const senderMatch = SENDER_RE.exec((rawContent as string) ?? '');
  const senderName = senderMatch?.[1]?.trim() ?? null;
  const content = senderMatch ? (rawContent as string).slice(senderMatch[0]?.length ?? 0) : ((rawContent as string) ?? '');
  const [toolExpanded, setToolExpanded] = useState(false);

  if (type === 'system') {
    return (
      <div className="flex justify-center py-1">
        <span className="text-xs italic text-muted-foreground">
          <MessageContent text={content} fileMap={fileMap} />
        </span>
      </div>
    );
  }

  if (type === 'tool') {
    const isLong = content.length > 80;
    return (
      <div className="flex justify-center py-1">
        <div className="max-w-[80%] rounded border border-border bg-muted/40 px-2 py-1 text-xs font-mono text-muted-foreground">
          {isLong && !toolExpanded ? (
            <>
              <span>{content.slice(0, 80)}…</span>
              <button
                onClick={() => setToolExpanded(true)}
                className="ml-1 underline underline-offset-2 hover:text-foreground"
              >
                show
              </button>
            </>
          ) : (
            <>
              <MessageContent text={content} fileMap={fileMap} />
              {isLong && (
                <button
                  onClick={() => setToolExpanded(false)}
                  className="ml-1 underline underline-offset-2 hover:text-foreground"
                >
                  hide
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const isHuman = type === 'human';

  return (
    <div className={`flex flex-col gap-0.5 ${isHuman ? 'items-end' : 'items-start'}`}>
      {isHuman && senderName && (
        <span className="mr-1 text-xs text-muted-foreground">{senderName}</span>
      )}
      {!isHuman && agentName && (
        <span className="ml-1 text-xs text-muted-foreground">{agentName}</span>
      )}
      <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm leading-relaxed ${
            isHuman
              ? 'rounded-br-sm bg-primary text-primary-foreground'
              : 'rounded-bl-sm bg-muted text-foreground'
          }`}
        >
          <MessageContent text={content} fileMap={fileMap} />
          {hasExtraData && (
            <p className="mt-1 text-[11px] opacity-60 italic">
              [Contains additional data — files stored externally]
            </p>
          )}
        </div>
      </div>
      <time
        className={`text-[11px] text-muted-foreground ${isHuman ? 'mr-1' : 'ml-1'}`}
        dateTime={message.created_at}
      >
        {relativeTime(message.created_at)}
      </time>
    </div>
  );
}
